import React from 'react';
import type { DiscountCode, DiscountCodeSortKey, SortDirection } from '../../../models/discountCode';

export interface DiscountCodeTableProps {
  /** Rows to render, already sorted and paginated by the parent. */
  codes: DiscountCode[];
  sortKey: DiscountCodeSortKey;
  sortDirection: SortDirection;
  onSort: (key: DiscountCodeSortKey) => void;
  onEdit: (code: DiscountCode) => void;
  onToggleStatus: (code: DiscountCode) => void;
  onDelete: (code: DiscountCode) => void;
}

function formatAmount(code: DiscountCode): string {
  return code.type === 'percentage' ? `${code.amount}%` : `$${code.amount.toFixed(2)}`;
}

function formatExpiration(value: string | null): string {
  if (!value) {
    return 'Never';
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleDateString(undefined, { timeZone: 'UTC' });
}

function statusPillClass(status: DiscountCode['status']): string {
  if (status === 'active') {
    return 'bo-status-active';
  }
  if (status === 'expired') {
    return 'bo-status-expired';
  }
  return 'bo-status-inactive';
}

function sortIndicator(active: boolean, direction: SortDirection): string {
  if (!active) {
    return '';
  }
  return direction === 'asc' ? ' ▲' : ' ▼';
}

export function DiscountCodeTable({
  codes,
  sortKey,
  sortDirection,
  onSort,
  onEdit,
  onToggleStatus,
  onDelete,
}: DiscountCodeTableProps): React.ReactElement {
  return (
    <div className="bo-card">
      <div className="bo-admin-table-head">
        <h2 className="bo-admin-section-title">Discount codes</h2>
        <p className="bo-muted">Manage promotions and billing incentives across 3PL companies.</p>
      </div>
      <div className="bo-admin-table-wrap">
        <table className="bo-admin-table">
          <thead>
            <tr>
              <th>
                <button type="button" className="bo-link-button" onClick={() => onSort('code')}>
                  Code{sortIndicator(sortKey === 'code', sortDirection)}
                </button>
              </th>
              <th>
                <button type="button" className="bo-link-button" onClick={() => onSort('type')}>
                  Type{sortIndicator(sortKey === 'type', sortDirection)}
                </button>
              </th>
              <th>
                <button type="button" className="bo-link-button" onClick={() => onSort('amount')}>
                  Amount{sortIndicator(sortKey === 'amount', sortDirection)}
                </button>
              </th>
              <th>
                <button type="button" className="bo-link-button" onClick={() => onSort('expiresAt')}>
                  Expiration{sortIndicator(sortKey === 'expiresAt', sortDirection)}
                </button>
              </th>
              <th>Assigned companies</th>
              <th>
                <button type="button" className="bo-link-button" onClick={() => onSort('status')}>
                  Status{sortIndicator(sortKey === 'status', sortDirection)}
                </button>
              </th>
              <th aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {codes.map((code) => {
              const isExpired = code.status === 'expired';
              return (
                <tr key={code.id}>
                  <td>
                    <strong>{code.code}</strong>
                  </td>
                  <td>{code.type === 'percentage' ? 'Percentage' : 'Fixed'}</td>
                  <td>{formatAmount(code)}</td>
                  <td>{formatExpiration(code.expiresAt)}</td>
                  <td>
                    <div className="bo-admin-order-mix">
                      {code.assignedCompanies.length === 0 ? (
                        <span className="bo-muted">—</span>
                      ) : (
                        code.assignedCompanies.map((company) => (
                          <span key={`${code.id}-${company.companyId}`} className="bo-admin-order-chip">
                            {company.companyName || company.companyId}
                          </span>
                        ))
                      )}
                    </div>
                  </td>
                  <td>
                    <span className={`bo-status-pill ${statusPillClass(code.status)}`}>{code.status}</span>
                  </td>
                  <td>
                    <div className="bo-actions">
                      <button type="button" className="bo-btn bo-btn-secondary" onClick={() => onEdit(code)}>
                        Edit
                      </button>
                      <button
                        type="button"
                        className="bo-btn bo-btn-secondary"
                        onClick={() => onToggleStatus(code)}
                        disabled={isExpired}
                        title={isExpired ? 'Expired codes cannot be reactivated' : undefined}
                      >
                        {code.status === 'active' ? 'Deactivate' : 'Activate'}
                      </button>
                      <button type="button" className="bo-btn bo-btn-danger" onClick={() => onDelete(code)}>
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
